// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@hardwoods/XDL/types";
import color from "@hardwoods/XDL/Utilities/color";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { css } from "styled-components";

sizeVariantValues.small = {
    ...sizeVariantValues.small,
    height: 35,
};

/**  label style */
export const labelStyle = css`
    label {
        font-size: 12px;
        letter-spacing: 0;
        line-height: 18px;

        &[aria-invalid="true"] {
            color: ${color("danger")};
        }
    }
`;

export const iconOffset = 32;
/**  formField general style */
const formFieldStyle = css`
    &&& {
        input,
        input[type="number"],
        input:focus,
        textarea,
        textarea:focus,
        select,
        select:focus {
            padding-left: 10px;
            padding-right: 35px;
            letter-spacing: 0;
            line-height: 18px;
            text-overflow: ellipsis;
            border-radius: 2px;
            box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)};

            border: 1px solid ${color("common.ui13")};

            &[aria-invalid="true"] {
                border-color: ${color("danger.main")};
            }

            &:hover {
                border-color: ${color("primary.hover")};
            }

            &:focus {
                border-color: ${color("primary.dark")};
            }

            &:disabled {
                border-color: ${color("common.ui13", 0.6)};
                color: ${color("text.secondary", 0.8)};
            }
        }
        textarea,
        textarea:focus {
            padding-top: 10px;
        }
        input[type="number"],
        input[type="number"]:focus {
            padding-right: 0;
        }

        input.react-datetime-picker__inputGroup__input,
        select.react-datetime-picker__inputGroup__input {
            border: none;
            box-shadow: none;
            padding: 0;
        }
    }
`;

/**  textarea general style */
const textareaStyle = css`
    textarea,
    textarea:focus {
        padding: 10px;
    }
    textarea {
        width: 100% !important /*Override Inline style on resize*/;
    }
    textarea:focus ~ label,
    textarea:not(:empty) ~ label {
        bottom: 100%;
        font-size: 12px;
    }
`;

/**  Select general style */
export const selectStyle = css`
    & select,
    select:focus {
        border-width: 1px;
        padding: 0 ${iconOffset}px 0 0;
    }

    select:disabled ~ [class^="IconWrapper"] {
        color: ${color("common.ui13", 0.33)};
    }
`;

/**  dropdwn general style */
const dropDownStyle = css`
    & div[role="combobox"] {
        width: 100%;
        &:disabled ~ [class^="IconWrapper"] {
            color: ${color("danger.main", 0.5)};
        }
    }
`;

export const generateFormFieldStyle = (type: "input" | "textarea" | "select" | "dropdown") => {
    let style;
    switch (type) {
        case "textarea":
            style = textareaStyle;
            break;
        case "select":
            style = selectStyle;
            break;
        case "dropdown":
            style = dropDownStyle;
            break;
    }
    return css` 
        ${labelStyle}
        ${formFieldStyle}              
        ${style}               
    `;
};

export default {
    defaultProps: {
        border: "rectangle",
        sizeVariant: "default",
        cssOverrides: {
            formInputWrapper: css``,
            inputSelect: css`
                background: ${color("common.ui4")};
            `,
            formField: css`
                ${labelStyle}
                ${formFieldStyle}
                
                ${textareaStyle}  
                ${selectStyle}
                ${dropDownStyle}
            `,

            descriptionWrapper: css`
                div[class^="FormInputWrapper"] > div[class^="DescriptionWrapper"] {
                    margin-top: 5px;
                }

                && > span {
                    font-size: 12px;
                    letter-spacing: 0;
                    line-height: 15px;
                }
            `,
        },
        labelProps: {
            size: 15,
            color: "text.secondary",
            weight: "normal",
        },
    },
} as xBaseTheme["formField"];
